import { useEffect, useState } from "react";
import { api, type ListerSessionSummary } from "../../lib/api";
import { hashFor } from "../../lib/routes";
import StatusPill from "../../components/StatusPill";

type Filter = "all" | "attention" | "posted" | "failed";
const DOT = { green: "bg-green-400", yellow: "bg-yellow-400", red: "bg-red-400" };

export default function History() {
  const [rows, setRows] = useState<ListerSessionSummary[]>([]);
  const [limit, setLimit] = useState(50);
  const [filter, setFilter] = useState<Filter>("all");
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api.listListerSessions(limit).then(setRows).catch((e) => setError(String(e)));
  }, [limit]);

  const shown = rows.filter((r) => {
    if (filter === "all") return true;
    if (filter === "posted" && r.status === "posted") return true;
    if (filter === "failed" && r.status === "failed") return true;
    if (filter === "attention" && r.verdict_level === "red") return true;
    return false;
  });

  return (
    <div className="max-w-3xl mx-auto px-4 py-4 space-y-3 font-mono text-sm">
      <div className="flex items-center justify-between">
        <h1 className="text-zinc-100 text-base">Lister · history</h1>
        <a href={hashFor.lister()} className="underline text-zinc-400">scan</a>
      </div>
      <div className="flex gap-2 text-xs">
        {(["all", "attention", "posted", "failed"] as Filter[]).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-2 py-1 rounded ${filter === f ? "bg-zinc-700 text-zinc-100" : "bg-zinc-800 text-zinc-400"}`}
          >
            {f === "attention" ? "Needs attention" : f[0]!.toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>
      {error && <p className="text-red-400">{error}</p>}
      <ul className="divide-y divide-zinc-800 border border-zinc-800 rounded-md">
        {shown.map((r) => (
          <li key={r.id} className="p-3 flex items-center gap-3">
            <span
              className={`w-2 h-2 rounded-full shrink-0 ${
                r.verdict_level ? DOT[r.verdict_level] : "bg-zinc-700"
              }`}
            />
            <div className="min-w-0 flex-1">
              <div className="text-zinc-100 truncate">{r.title ?? r.gtin ?? "card"}</div>
              <div className="text-xs text-zinc-500 truncate">
                {r.gtin ?? (r.mode === "card" ? "card" : "—")} · {new Date(r.created_at).toLocaleString()}
                {r.error && <span className="text-red-400"> · {r.error}</span>}
              </div>
            </div>
            <StatusPill status={r.status} />
            <a href={hashFor.review(r.id)} className="text-xs underline text-zinc-300">
              open
            </a>
            {r.admin_url && (
              <a
                href={r.admin_url}
                target="_blank"
                rel="noopener"
                className="text-xs underline text-zinc-400"
              >
                admin
              </a>
            )}
          </li>
        ))}
      </ul>
      {rows.length >= limit && (
        <button
          onClick={() => setLimit(limit + 50)}
          className="bg-zinc-800 hover:bg-zinc-700 px-3 py-2 rounded"
        >
          load more
        </button>
      )}
    </div>
  );
}
